import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { segmentApi } from '@edulastic/api'
import { EduJoyride } from '@edulastic/common'
import {
  EDUCATION_JOYRIDE_SEGMENT_CTA,
  TRACK_IDS,
} from '@edulastic/constants/educationJoyrides'
import { ACTIONS, EVENTS, LIFECYCLE } from 'react-joyride'
import {
  buildTrackStateAfterDismiss,
  resolveJoyrideVariant,
} from './resolveJoyrideVariant'
import {
  adjustEducationJoyrideSpotlight,
  observeEducationJoyrideSpotlight,
} from './adjustJoyrideSpotlight'
import {
  EDUCATION_JOYRIDE_LOCALE,
  EDUCATION_JOYRIDE_SPOTLIGHT_PADDING,
  EDUCATION_JOYRIDE_TARGET_WAIT_MS,
  getEducationJoyrideStyles,
} from './constants'
import { getTrackConfig } from './tracks'
import EducationJoyrideTooltip from './EducationJoyrideTooltip'
import { getModerateTrackContent } from './moderateTrackContent'
import {
  educationJoyridesLoadingSelector,
  educationJoyridesTracksSelector,
  storeEducationJoyridesAction,
} from '../../../../educationJoyrides/educationJoyridesReducer'

const focusJoyrideTarget = (targetSelector) => {
  const targetEl = document.querySelector(targetSelector)
  if (!targetEl) return

  if (targetEl instanceof HTMLElement) {
    if (!targetEl.hasAttribute('tabindex')) {
      targetEl.setAttribute('tabindex', '-1')
    }
    targetEl.focus({ preventScroll: true, focusVisible: false })
  }
}

const EducationJoyride = ({
  trackId,
  featureGate = true,
  legacyBlocked = false,
  layoutBlocked = false,
  contextsLoading: contextsLoadingProp,
  tracks,
  storeEducationJoyrides,
  joyridesLoading,
  contextsLoadingFromState,
  targetWaitMs = EDUCATION_JOYRIDE_TARGET_WAIT_MS,
  onBeforeStart,
  onRunChange,
  onDismiss,
}) => {
  const contextsLoading = contextsLoadingProp ?? contextsLoadingFromState
  const [run, setRun] = useState(false)
  const skippedPersistRef = useRef(false)

  const trackConfig = useMemo(() => getTrackConfig(trackId), [trackId])
  const trackState = tracks?.[trackId]
  const variant = useMemo(() => resolveJoyrideVariant(trackState), [trackState])

  const shouldEvaluate =
    featureGate &&
    !legacyBlocked &&
    !layoutBlocked &&
    !contextsLoading &&
    trackConfig

  useEffect(() => {
    onRunChange?.(run)
  }, [run, onRunChange])

  useEffect(() => {
    if (
      !shouldEvaluate ||
      variant !== 'moderate_skipped' ||
      skippedPersistRef.current
    ) {
      return
    }

    skippedPersistRef.current = true
    const nextTrackState = buildTrackStateAfterDismiss({
      trackState,
      variant: 'moderate_skipped',
    })
    storeEducationJoyrides({
      tracks: {
        ...tracks,
        [trackId]: nextTrackState,
      },
    })
  }, [
    shouldEvaluate,
    variant,
    trackId,
    trackState,
    tracks,
    storeEducationJoyrides,
  ])

  useEffect(() => {
    if (
      !shouldEvaluate ||
      !variant ||
      variant === 'moderate_skipped' ||
      joyridesLoading
    ) {
      setRun(false)
      return undefined
    }

    const { target } = trackConfig
    let cancelled = false
    let hasStarted = false
    let timeoutId
    let observer

    const stopObserving = () => {
      observer?.disconnect()
      clearTimeout(timeoutId)
    }

    const startWhenTargetReady = async () => {
      if (hasStarted || cancelled) return false

      const targetEl = document.querySelector(target)
      if (!targetEl) return false

      if (onBeforeStart) {
        await onBeforeStart()
      }
      if (cancelled) return false

      hasStarted = true
      targetEl.scrollIntoView({ behavior: 'instant', block: 'center' })
      setRun(true)
      stopObserving()
      return true
    }

    startWhenTargetReady()

    observer = new MutationObserver(() => {
      startWhenTargetReady()
    })
    observer.observe(document.body, { childList: true, subtree: true })

    timeoutId = setTimeout(() => {
      if (!hasStarted) {
        setRun(false)
      }
      stopObserving()
    }, targetWaitMs)

    return () => {
      cancelled = true
      stopObserving()
    }
  }, [
    shouldEvaluate,
    variant,
    trackConfig,
    joyridesLoading,
    targetWaitMs,
    onBeforeStart,
  ])

  useEffect(() => {
    if (!run || !trackConfig?.target || variant !== 'aggressive') {
      return undefined
    }

    return observeEducationJoyrideSpotlight(trackConfig.target, {
      padding: EDUCATION_JOYRIDE_SPOTLIGHT_PADDING,
      useOuterTarget: trackConfig.spotlightUsesOuterTarget,
    })
  }, [run, trackConfig, variant])

  const persistDismiss = useCallback(
    (dismissVariant, userResponse) => {
      onDismiss?.()
      const nextTrackState = buildTrackStateAfterDismiss({
        trackState,
        variant: dismissVariant,
      })
      storeEducationJoyrides({
        tracks: {
          ...tracks,
          [trackId]: nextTrackState,
        },
      })
      segmentApi.genericEventTrack('Joyride', {
        userResponse,
        CTA_Name: EDUCATION_JOYRIDE_SEGMENT_CTA[trackId],
        pagePath: window.location.pathname,
        popup_occurrence: dismissVariant === 'aggressive' ? 1 : 2,
        joyride_track: trackId,
        joyride_variant: dismissVariant,
      })
      window.setTimeout(() => {
        setRun(false)
        if (trackId !== TRACK_IDS.CURRICULUM_PA) {
          focusJoyrideTarget(trackConfig?.target)
        }
      }, 0)
    },
    [
      trackId,
      trackState,
      tracks,
      storeEducationJoyrides,
      onDismiss,
      trackConfig,
    ]
  )

  const handleCallback = useCallback(
    (data) => {
      if (data.type === EVENTS.TARGET_NOT_FOUND) {
        setRun(false)
        return
      }

      if (
        variant === 'aggressive' &&
        (data.type === EVENTS.TOUR_START ||
          data.type === EVENTS.STEP_BEFORE ||
          data.type === EVENTS.STEP_AFTER)
      ) {
        adjustEducationJoyrideSpotlight(trackConfig.target, {
          padding: EDUCATION_JOYRIDE_SPOTLIGHT_PADDING,
          useOuterTarget: trackConfig.spotlightUsesOuterTarget,
        })
      }

      if (data.lifecycle !== LIFECYCLE.COMPLETE) return

      const { CLOSE, NEXT, LAST } = ACTIONS
      const isCtaComplete = data.action === NEXT || data.action === LAST
      const isClose = data.action === CLOSE

      if (variant === 'aggressive') {
        if (!isCtaComplete && !isClose) return
        persistDismiss('aggressive', isClose ? 'Close' : 'clickCTA')
        return
      }

      if (variant === 'moderate') {
        if (!isClose && !isCtaComplete) return
        persistDismiss('moderate', isClose ? 'Close' : 'clickCTA')
      }
    },
    [variant, persistDismiss, trackConfig]
  )

  const titleId = `education-joyride-title-${trackId}`
  const descId = `education-joyride-desc-${trackId}`
  const moderateContent = useMemo(() => getModerateTrackContent(trackId), [
    trackId,
  ])

  const renderTooltip = useCallback(
    (tooltipProps) => (
      <EducationJoyrideTooltip
        {...tooltipProps}
        variant={variant}
        moderateIcon={moderateContent?.Icon}
        titleId={titleId}
        descId={descId}
        onDismissInteraction={onDismiss}
      />
    ),
    [variant, moderateContent, titleId, descId, onDismiss]
  )

  if (
    !shouldEvaluate ||
    !variant ||
    variant === 'moderate_skipped' ||
    !trackConfig
  ) {
    return null
  }

  const steps = [
    trackConfig.buildStep({
      target: trackConfig.target,
      variant,
      titleId,
      descId,
    }),
  ]

  return (
    <EduJoyride
      variant={variant}
      hideCloseButton
      run={run}
      steps={steps}
      callback={handleCallback}
      locale={EDUCATION_JOYRIDE_LOCALE}
      styles={getEducationJoyrideStyles(variant)}
      spotlightPadding={
        variant === 'aggressive' ? EDUCATION_JOYRIDE_SPOTLIGHT_PADDING : 0
      }
      spotlightClicks={false}
      tooltipComponent={renderTooltip}
      options={{ zIndex: 1015 }}
    />
  )
}

export default connect(
  (state) => ({
    tracks: educationJoyridesTracksSelector(state),
    joyridesLoading: educationJoyridesLoadingSelector(state),
    contextsLoadingFromState:
      state.dashboardTeacher?.userContextLoading ?? false,
  }),
  {
    storeEducationJoyrides: storeEducationJoyridesAction,
  }
)(EducationJoyride)
